import { Box, Flex, Paper, Text, Button, Title } from "@mantine/core";
import {
  IconBuilding,
  IconPhone,
  IconMail,
  IconBookmark,
  IconRosetteDiscountCheckFilled,
  IconCheck,
  IconX,
  IconDownload,
} from "@tabler/icons-react";
import ProfileVerificationStepper from "@/pages/profile-verification/ProfileVerificationStepper";
import { getCollaborationStatusConfig } from "@/shared/utils/collaboration-helpers";
import { STATUS_COLORS } from "@/components/shared/constants/general.const";
import CompanyProfileHeaderLayout from "./CompanyProfileHeaderLayout";
import { COMPANY_HEADER_NAME_DETAILS_GAP_PX } from "./companyHeaderLayout.const";
import { ANONYMIZED_MASK } from "@/shared/utils/anonymizedProfileDisplay";
import { PartnershipHandshakeIcon } from "@/components/icons/PartnershipHandshakeIcon";

const INCOMING_REQUEST_BANNER_STYLE = {
  border: `1px solid ${STATUS_COLORS.WARNING.border}`,
  backgroundColor: STATUS_COLORS.WARNING.bg,
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
} as const;

const INCOMING_REQUEST_BANNER_TITLE = "Review the collaboration details and take the next step.";
const INCOMING_REQUEST_BANNER_SUBTITLE =
  "Accept the request to create a mutual match and start collaborating.";

const INCOMING_REQUEST_ACCEPT_BTN_STYLES = {
  root: {
    backgroundColor: "#04305c",
    borderColor: "#04305c",
    color: "#ffffff",
    fontWeight: 500,
    "&:hover": { backgroundColor: "#032847", borderColor: "#032847" },
  },
};

const INCOMING_REQUEST_SECONDARY_BTN_STYLES = {
  root: {
    borderColor: "#D1D5DB",
    color: "#374151",
    backgroundColor: "transparent",
    fontWeight: 500,
    "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.45)" },
  },
};

type CompanyHeaderCardProps = {
  company: any;
  headquartersFlagEmoji: string | null;
  showProfileVerificationStepper: boolean;
  hideInterestCTA: boolean;
  /** Logged-in viewer without collaboration — show Download Profile PDF in header. */
  showDownloadProfilePdf?: boolean;
  disableShortlist: boolean;
  isFavorited: boolean;
  collaborationStatus: any;
  userCompanyId?: string;
  canIncomingReceiverDownloadAnonymizedPdf: boolean;
  anonymizedPdfLoading: boolean;
  declineLoading: boolean;
  onExpressInterestClick: () => void;
  onShortlistClick: () => void;
  onAcceptRequestClick: () => void;
  onDeclineClick: () => void;
  onDownloadAnonymizedProfilePdf: () => void;
  bookmarkButtonStyles: Record<string, unknown>;
  /** Collaboration workspace: single light-blue card, GTID + inline contact + status chip (all breakpoints). */
  compactWorkspace?: boolean;
};

const LOGO_MOBILE = 48;
const LOGO_DESKTOP = 80;

function CompanyLogo({ size }: { size: number }) {
  return (
    <Flex
      w={size}
      h={size}
      style={{
        borderRadius: 8,
        border: size >= LOGO_DESKTOP ? "1px solid #04305c" : "1px solid #8EA6CF",
        backgroundColor: size >= LOGO_DESKTOP ? "#F9FAFB" : "#F3F6FC",
        flexShrink: 0,
        overflow: "hidden",
      }}
      align="center"
      justify="center"
    >
      <IconBuilding size={size >= LOGO_DESKTOP ? 40 : 24} color="#6B7280" />
    </Flex>
  );
}

function GtidTitle({ company, fz }: { company: any; fz?: number | string }) {
  return (
    <Flex align="center" gap={6} wrap="wrap">
      <Text size={fz ? undefined : "xl"} fz={fz} fw={fz ? 400 : 500} c="#334155" style={{ lineHeight: 1.2 }}>
        {company.gtid}
      </Text>
      {company.status === "VERIFIED" && (
        <IconRosetteDiscountCheckFilled
          size={fz ? 20 : 18}
          color="var(--mantine-color-primary-6)"
        />
      )}
    </Flex>
  );
}

export default function CompanyHeaderCard({
  company,
  headquartersFlagEmoji,
  showProfileVerificationStepper,
  hideInterestCTA,
  showDownloadProfilePdf = false,
  disableShortlist,
  isFavorited,
  collaborationStatus,
  userCompanyId,
  canIncomingReceiverDownloadAnonymizedPdf,
  anonymizedPdfLoading,
  declineLoading,
  onExpressInterestClick,
  onShortlistClick,
  onAcceptRequestClick,
  onDeclineClick,
  onDownloadAnonymizedProfilePdf,
  bookmarkButtonStyles,
  compactWorkspace = false,
}: CompanyHeaderCardProps) {
  const isIncomingReceiverRequest =
    collaborationStatus?.status === "REQUESTED" &&
    userCompanyId === collaborationStatus.receiverCompanyId;

  const downloadProfilePdfButton = (size: "sm" | "md" = "md", fullWidth = false) => (
    <Button
      variant="outline"
      color="primary"
      size={size}
      fullWidth={fullWidth}
      leftSection={<IconDownload size={size === "sm" ? 16 : 18} />}
      loading={anonymizedPdfLoading}
      onClick={onDownloadAnonymizedProfilePdf}
    >
      Download Profile PDF
    </Button>
  );

  const renderIncomingReceiverActions = (size: "sm" | "md") => {
    const iconSize = size === "sm" ? 16 : 18;
    return (
      <Flex
        gap="sm"
        align="center"
        wrap="wrap"
        justify={{ base: "stretch", sm: "flex-end" }}
        w={{ base: "100%", sm: "auto" }}
        style={{ flexShrink: 0 }}
      >
        <Button
          size={size}
          leftSection={<IconCheck size={iconSize} />}
          onClick={onAcceptRequestClick}
          styles={INCOMING_REQUEST_ACCEPT_BTN_STYLES}
          style={{ flex: size === "sm" ? 1 : undefined }}
        >
          Accept
        </Button>
        <Button
          size={size}
          variant="outline"
          leftSection={<IconX size={iconSize} />}
          onClick={onDeclineClick}
          loading={declineLoading}
          styles={INCOMING_REQUEST_SECONDARY_BTN_STYLES}
          style={{ flex: size === "sm" ? 1 : undefined }}
        >
          Decline
        </Button>
        {canIncomingReceiverDownloadAnonymizedPdf ? (
          <Button
            variant="outline"
            size={size}
            fullWidth={size === "sm"}
            leftSection={<IconDownload size={iconSize} />}
            loading={anonymizedPdfLoading}
            onClick={onDownloadAnonymizedProfilePdf}
            styles={INCOMING_REQUEST_SECONDARY_BTN_STYLES}
          >
            Download Profile PDF
          </Button>
        ) : null}
        {!disableShortlist ? (
          <Button variant="filled" size={size} p="xs" onClick={onShortlistClick} styles={bookmarkButtonStyles}>
            <IconBookmark size={size === "sm" ? 20 : 24} color="white" fill={isFavorited ? "currentColor" : "none"} />
          </Button>
        ) : null}
      </Flex>
    );
  };

  if (isIncomingReceiverRequest) {
    return (
      <Paper p="md" radius="lg" mb="md" style={INCOMING_REQUEST_BANNER_STYLE}>
        <Flex
          justify="space-between"
          align={{ base: "stretch", sm: "center" }}
          gap="md"
          direction={{ base: "column", sm: "row" }}
          wrap="nowrap"
        >
          <Flex gap="sm" align="flex-start" miw={0} style={{ flex: 1 }}>
            <Flex
              w={48}
              h={48}
              align="center"
              justify="center"
              style={{
                borderRadius: "50%",
                backgroundColor: "#FDE68A",
                flexShrink: 0,
              }}
            >
              <PartnershipHandshakeIcon size={26} style={{ color: "#0F172A" }} />
            </Flex>
            <Box miw={0}>
              <Text fw={700} size="sm" c="#0F172A" lh={1.35}>
                {INCOMING_REQUEST_BANNER_TITLE}
              </Text>
              <Text size="sm" c="#0F172A" mt={4} lh={1.45}>
                {INCOMING_REQUEST_BANNER_SUBTITLE}
              </Text>
            </Box>
          </Flex>
          <Box hiddenFrom="sm" w="100%">
            {renderIncomingReceiverActions("sm")}
          </Box>
          <Box visibleFrom="sm">{renderIncomingReceiverActions("md")}</Box>
        </Flex>
      </Paper>
    );
  }
  const renderMobileCollaborationChip = () => {
    if (!collaborationStatus) return null;
    if (
      collaborationStatus.status === "PAYMENT_EXPIRED" ||
      collaborationStatus.status === "BOTH_PAID" ||
      collaborationStatus.status === "RECEIVER_DECLINED"
    ) {
      return null;
    }

    const isReceiver = userCompanyId === collaborationStatus.receiverCompanyId;
    const isRequested = collaborationStatus.status === "REQUESTED";
    if (isRequested && isReceiver) return null;

    const chipConfig = userCompanyId
      ? getCollaborationStatusConfig(
        collaborationStatus.status,
        userCompanyId === collaborationStatus.requesterCompanyId,
        collaborationStatus.requesterPaid,
        collaborationStatus.receiverPaid
      )
      : getCollaborationStatusConfig(collaborationStatus.status);

    return (
      <Box
        px={10}
        py={5}
        style={{
          borderRadius: "var(--mantine-radius-md)",
          backgroundColor: chipConfig.bg,
          color: chipConfig.text,
          fontSize: "12px",
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {chipConfig.label}
      </Box>
    );
  };

  const renderIncomingRequestActionsMobile = () => {
    if (
      !collaborationStatus ||
      collaborationStatus.status === "PAYMENT_EXPIRED" ||
      collaborationStatus.status === "BOTH_PAID" ||
      collaborationStatus.status === "RECEIVER_DECLINED"
    ) {
      return null;
    }

    const isReceiver = userCompanyId === collaborationStatus.receiverCompanyId;
    const isRequested = collaborationStatus.status === "REQUESTED";
    if (!(isRequested && isReceiver)) return null;

    return (
      <Flex direction="column" gap="xs" w="100%" mt="sm">
        <Flex gap="xs" w="100%">
          <Button size="xs" color="primary" leftSection={<IconCheck size={16} />} onClick={onAcceptRequestClick} style={{ flex: 1 }}>
            Accept
          </Button>
          <Button
            size="xs"
            variant="outline"
            leftSection={<IconX size={16} />}
            onClick={onDeclineClick}
            loading={declineLoading}
            style={{ flex: 1 }}
          >
            Decline
          </Button>
        </Flex>
        {canIncomingReceiverDownloadAnonymizedPdf && (
          <Button
            variant="outline"
            color="primary"
            size="xs"
            fullWidth
            leftSection={<IconDownload size={16} />}
            loading={anonymizedPdfLoading}
            onClick={onDownloadAnonymizedProfilePdf}
          >
            Download Profile PDF
          </Button>
        )}
      </Flex>
    );
  };

  const countryRow = (
    <Flex align="center" gap={8} wrap="nowrap" miw={0}>
      {headquartersFlagEmoji ? (
        <Text
          component="span"
          fz={16}
          lh={1}
          role="img"
          aria-label={`${company.headquartersCountry ?? "Country"} flag`}
          style={{ flexShrink: 0 }}
        >
          {headquartersFlagEmoji}
        </Text>
      ) : null}
      <Text size="sm" c="slate.7" style={{ minWidth: 0, wordBreak: "break-word" }}>
        {company.headquartersCountry || "India"}
      </Text>
    </Flex>
  );

  const mobileContactRow = (
    <Flex gap="md" wrap="wrap" align="center" w="100%">
      {countryRow}
      <Flex align="center" gap={6} wrap="nowrap" miw={0} style={{ flexShrink: 0 }}>
        <IconPhone size={14} color="#6B7280" style={{ flexShrink: 0 }} />
        <Text size="sm" c="slate.7" style={{ minWidth: 0, wordBreak: "break-word" }}>
          {ANONYMIZED_MASK}
        </Text>
      </Flex>
      <Flex align="center" gap={6} wrap="nowrap" miw={0} style={{ flexShrink: 0 }}>
        <IconMail size={14} color="#6B7280" style={{ flexShrink: 0 }} />
        <Text size="sm" c="slate.7" style={{ minWidth: 0, wordBreak: "break-all" }}>
          {ANONYMIZED_MASK}
        </Text>
      </Flex>
    </Flex>
  );

  const mobileVerificationNode = showProfileVerificationStepper ? (
    <ProfileVerificationStepper status={company.status} compact align="center" compactLayout="column" />
  ) : null;

  const desktopVerificationNode = showProfileVerificationStepper ? (
    <ProfileVerificationStepper status={company.status} compact compactLayout="column" align="center" />
  ) : null;

  const mobileTopActions = (
    <Flex direction="column" gap="xs" align="flex-end">
      {mobileVerificationNode}
      {!hideInterestCTA && (collaborationStatus ? renderMobileCollaborationChip() : null)}
      {!disableShortlist && (
        <Button
          variant="filled"
          size="md"
          p="xs"
          onClick={onShortlistClick}
          styles={bookmarkButtonStyles}
          style={{ flexShrink: 0 }}
        >
          <IconBookmark size={20} color="white" fill={isFavorited ? "currentColor" : "none"} />
        </Button>
      )}
    </Flex>
  );

  const renderCollaborationStatusChip = () => {
    if (
      !collaborationStatus ||
      collaborationStatus.status === "PAYMENT_EXPIRED" ||
      collaborationStatus.status === "BOTH_PAID" ||
      collaborationStatus.status === "RECEIVER_DECLINED"
    ) {
      return null;
    }

    const isReceiver = userCompanyId === collaborationStatus.receiverCompanyId;
    const isRequested = collaborationStatus.status === "REQUESTED";
    if (isRequested && isReceiver) return null;

    const chipConfig = userCompanyId
      ? getCollaborationStatusConfig(
        collaborationStatus.status,
        userCompanyId === collaborationStatus.requesterCompanyId,
        collaborationStatus.requesterPaid,
        collaborationStatus.receiverPaid
      )
      : getCollaborationStatusConfig(collaborationStatus.status);

    return (
      <Box
        px={16}
        py={7}
        style={{
          borderRadius: "var(--mantine-radius-md)",
          backgroundColor: chipConfig.bg,
          color: chipConfig.text,
          fontSize: "13px",
          fontWeight: 500,
          whiteSpace: "nowrap",
        }}
      >
        {chipConfig.label}
      </Box>
    );
  };

  const renderDesktopActions = () => {
    if (hideInterestCTA) {
      return !disableShortlist ? (
        <Button variant="filled" size="md" p="xs" onClick={onShortlistClick} styles={bookmarkButtonStyles}>
          <IconBookmark size={24} color="white" fill={isFavorited ? "currentColor" : "none"} />
        </Button>
      ) : null;
    }

    if (!collaborationStatus) {
      return (
        <Flex direction="row" align="center" gap="sm" wrap="wrap" justify="flex-end">
          {showDownloadProfilePdf ? downloadProfilePdfButton("md") : null}
          <Button size="md" color="primary" onClick={onExpressInterestClick}>
            Express Interest
          </Button>
          {!disableShortlist && (
            <Button variant="filled" size="md" p="xs" onClick={onShortlistClick} styles={bookmarkButtonStyles}>
              <IconBookmark size={24} color="white" fill={isFavorited ? "currentColor" : "none"} />
            </Button>
          )}
        </Flex>
      );
    }

    return (
      <Flex direction="row" align="center" gap="sm" wrap="nowrap">
        {collaborationStatus.status !== "PAYMENT_EXPIRED" &&
          collaborationStatus.status !== "BOTH_PAID" &&
          collaborationStatus.status !== "RECEIVER_DECLINED" &&
          (() => {
            const isReceiver = userCompanyId === collaborationStatus.receiverCompanyId;
            const isRequested = collaborationStatus.status === "REQUESTED";

            if (isRequested && isReceiver) {
              return (
                <Flex gap="sm" align="center" wrap="wrap" justify="flex-end">
                  <Button color="primary" leftSection={<IconCheck size={20} />} onClick={onAcceptRequestClick}>
                    Accept
                  </Button>
                  <Button variant="outline" color="gray" leftSection={<IconX size={20} />} onClick={onDeclineClick} loading={declineLoading}>
                    Decline
                  </Button>
                  {canIncomingReceiverDownloadAnonymizedPdf && (
                    <Button
                      variant="outline"
                      color="primary"
                      size="md"
                      leftSection={<IconDownload size={18} />}
                      loading={anonymizedPdfLoading}
                      onClick={onDownloadAnonymizedProfilePdf}
                    >
                      Download Profile PDF
                    </Button>
                  )}
                </Flex>
              );
            }

            return renderCollaborationStatusChip();
          })()}
        {!disableShortlist && (
          <Button variant="filled" size="md" p="xs" onClick={onShortlistClick} styles={bookmarkButtonStyles}>
            <IconBookmark size={24} color="white" fill={isFavorited ? "currentColor" : "none"} />
          </Button>
        )}
      </Flex>
    );
  };

  const compactContactRow = (
    <Flex gap="md" wrap="wrap" align="center">
      {countryRow}
      <Flex align="center" gap={6} wrap="nowrap" miw={0} style={{ flexShrink: 0 }}>
        <IconPhone size={14} color="#6B7280" style={{ flexShrink: 0 }} />
        <Text size="sm" c="slate.7">
          {ANONYMIZED_MASK}
        </Text>
      </Flex>
      <Flex align="center" gap={6} wrap="nowrap" miw={0} style={{ flexShrink: 0 }}>
        <IconMail size={14} color="#6B7280" style={{ flexShrink: 0 }} />
        <Text size="sm" c="slate.7">
          {ANONYMIZED_MASK}
        </Text>
      </Flex>
    </Flex>
  );

  if (compactWorkspace && !isIncomingReceiverRequest) {
    return (
      <Paper
        p="md"
        radius="lg"
        mb="md"
        style={{
          border: "1px solid #9CB1D8",
          backgroundColor: "#E9EEFA",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        <Flex justify="space-between" align="center" gap="md" wrap="wrap">
          <Flex gap="md" align="flex-start" miw={0} style={{ flex: 1 }}>
            <CompanyLogo size={LOGO_MOBILE} />
            <Flex
              direction="column"
              miw={0}
              style={{ flex: 1, gap: COMPANY_HEADER_NAME_DETAILS_GAP_PX }}
            >
              <Flex align="center" gap={6} wrap="wrap">
                <Text fz="xl" fw={700} c="#04305c" style={{ lineHeight: 1.25, wordBreak: "break-word" }}>
                  {company.gtid}
                </Text>
                {company.status === "VERIFIED" && (
                  <IconRosetteDiscountCheckFilled size={20} color="var(--mantine-color-primary-6)" />
                )}
              </Flex>
              {compactContactRow}
            </Flex>
          </Flex>
          <Flex align="center" gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
            {!hideInterestCTA && collaborationStatus ? renderCollaborationStatusChip() : null}
            {!disableShortlist ? (
              <Button
                variant="filled"
                size="md"
                p="xs"
                onClick={onShortlistClick}
                styles={bookmarkButtonStyles}
                aria-label={isFavorited ? "Remove from shortlist" : "Add to shortlist"}
              >
                <IconBookmark size={24} color="white" fill={isFavorited ? "currentColor" : "none"} />
              </Button>
            ) : null}
          </Flex>
        </Flex>
      </Paper>
    );
  }

  return (
    <>
      <Paper
        p="md"
        radius="lg"
        mb="md"
        style={{
          border: "1px solid #9CB1D8",
          backgroundColor: "#E9EEFA",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
          maxWidth: "100%",
          overflow: "hidden",
        }}
        hiddenFrom="sm"
      >
        <CompanyProfileHeaderLayout
          logo={<CompanyLogo size={LOGO_MOBILE} />}
          title={<GtidTitle company={company} />}
          topActions={mobileTopActions}
          country={mobileContactRow}
          afterDetails={
            <>
              {showDownloadProfilePdf && !collaborationStatus ? (
                <Box mt="sm">{downloadProfilePdfButton("sm", true)}</Box>
              ) : null}
              {!hideInterestCTA && !collaborationStatus ? (
                <Button
                  fullWidth
                  mt="sm"
                  color="primary"
                  radius="md"
                  size="sm"
                  onClick={onExpressInterestClick}
                  styles={{
                    root: {
                      backgroundColor: "#04305c",
                      border: "1px solid #04305c",
                      color: "#ffffff",
                      fontWeight: 600,
                    },
                  }}
                >
                  Express Interest
                </Button>
              ) : null}
              {renderIncomingRequestActionsMobile()}
            </>
          }
        />
      </Paper>

      <Paper
        p="xl"
        radius="lg"
        mb="lg"
        style={{
          border: "1px solid #04305c",
          backgroundColor: "#ebf0ff",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
        }}
        visibleFrom="sm"
      >
        <Flex gap="xl" align="flex-start" justify="space-between">
          <Flex gap="md" align="flex-start">
            <CompanyLogo size={LOGO_DESKTOP} />
            <Flex direction="column" style={{ gap: COMPANY_HEADER_NAME_DETAILS_GAP_PX }}>
              <Flex align="center" gap="sm" wrap="wrap">
                <Title order={2} fw={400} fz={22}>
                  {company.gtid}
                </Title>
                {company.status === "VERIFIED" && (
                  <IconRosetteDiscountCheckFilled size={20} color="var(--mantine-color-primary-6)" />
                )}
              </Flex>
              <Flex gap="md" mb="xs" wrap="wrap">
                <Flex align="center" gap="xs">
                  {headquartersFlagEmoji ? (
                    <Text component="span" fz={20} lh={1} role="img" aria-label={`${company.headquartersCountry ?? "Country"} flag`}>
                      {headquartersFlagEmoji}
                    </Text>
                  ) : null}
                  <Text size="l" c="slate.7">
                    {company.headquartersCountry || "N/A"}
                  </Text>
                </Flex>
                <Flex align="center" gap="xs">
                  <IconPhone size={20} color="#6B7280" />
                  <Text size="l" c="slate.7">
                    {ANONYMIZED_MASK}
                  </Text>
                </Flex>
                <Flex align="center" gap="xs">
                  <IconMail size={20} color="#6B7280" />
                  <Text size="l" c="slate.7">
                    {ANONYMIZED_MASK}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Flex>

          <Flex direction="column" align="flex-end" gap="sm" style={{ flexShrink: 0 }}>
            {desktopVerificationNode}
            {renderDesktopActions()}
          </Flex>
        </Flex>
      </Paper>
    </>
  );
}
